import { useState, useEffect, useRef } from 'react'
import req from '@sportninja/common/api/request'

export interface TeamTreeNode {
  id: string
  name: string
  name_full: string
  abbreviation: string
  image_url: string | null
  image: any
  organization_type_id: number
  children_count: number
  children?: TeamTreeNode[]
  membership?: {
    role_id: number
    role_name: string
  }
  roles?: {
    role_id: number
    role_name: string
  }[]
}

interface UseTeamsTreeReturn {
  data: TeamTreeNode[] | null
  isLoading: boolean
  error: Error | null
}

/**
 * Check if team should be displayed
 * Only show teams where user has role_id 6 (player) or 7 (team official)
 */
const shouldShowTeam = (team: TeamTreeNode): boolean => {
  const roles = team.roles || []
  return roles.some((role) => role.role_id === 6 || role.role_id === 7)
}

/**
 * Recursively filter teams tree to only include player/team official roles
 */
const filterTeamsTree = (teams: TeamTreeNode[]): TeamTreeNode[] => {
  return teams.filter(shouldShowTeam).map((team) => ({
    ...team,
    children: team.children ? filterTeamsTree(team.children) : undefined,
  }))
}

/**
 * Hook to fetch the user's teams tree hierarchy
 * Uses req directly with a ref flag to prevent duplicate API calls
 */
export function useTeamsTree(): UseTeamsTreeReturn {
  const [data, setData] = useState<TeamTreeNode[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const fetchedRef = useRef(false)

  useEffect(() => {
    if (fetchedRef.current) {
      return
    }
    fetchedRef.current = true

    const fetchTree = async () => {
      try {
        const response = await req(
          '/users/me2/team-membership?page=1&sort=name&direction=asc&membership_type=permission&recursive=0'
        )
        const filteredData = filterTeamsTree(response.data)
        setData(filteredData)
      } catch (err) {
        setError(err as Error)
      } finally {
        setIsLoading(false)
      }
    }
    fetchTree()
  }, [])

  return { data, isLoading, error }
}

export default useTeamsTree
